import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, Loader2, RefreshCw } from 'lucide-react';
import { trainingApi, type TrainingPipeline, type TrainingPipelineNode } from '../../services/api';
import Waiting, { StatusPill } from './Waiting';
import { useAppStore } from '../../store/useAppStore';

function OpenItem({ id }: { id: string }) {
  const open = useAppStore((st) => st.setDetailItemId);
  return (
    <button type="button" onClick={() => open(id)} className="text-xs text-accent hover:underline">Open</button>
  );
}

/**
 * The Pipeline (EP-26 FE-5 ST-5): every campaign of the programme, its batches and
 * rounds, and the gate each waits on and why. The "why" is the server's dry run of
 * the item's next move — the same guards the move itself checks — so this page
 * and the work items cannot disagree.
 */
function NodeRow({ node }: { node: TrainingPipelineNode }) {
  const open = useAppStore((s) => s.setDetailItemId);
  return (
    <li className="py-1.5 grid gap-1 md:grid-cols-[minmax(0,14rem),8rem,1fr] md:items-start">
      <button type="button" onClick={() => open(node.id)} title={`Open ${node.title}: move, assign, check out`}
        className="text-left text-sm text-gray-900 dark:text-gray-100 truncate hover:text-accent">
        <span className="text-xs text-gray-500 mr-1">{node.key}</span>{node.title}
      </button>
      <span><StatusPill label={node.statusLabel} color={node.color} /></span>
      <Waiting waiting={node.waiting} />
    </li>
  );
}

export default function PipelinePanel({ programId }: { programId: string }) {
  const [data, setData] = useState<TrainingPipeline | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    trainingApi.pipeline(programId)
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(() => { setData(null); load(); }, [load]);

  const states = useMemo(() => {
    const seen = new Map<string, string>();
    (data?.campaigns || []).forEach((c) => seen.set(c.status, c.statusLabel));
    return [...seen.entries()];
  }, [data]);

  const shown = (data?.campaigns || []).filter((c) => !state || c.status === state);

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <GitBranch className="w-4 h-4" /> Pipeline
        </h2>
        <div className="flex items-center gap-3">
          <label htmlFor="pipeline-state" className="text-xs text-gray-500">Campaign state</label>
          <select id="pipeline-state" value={state} onChange={(e) => setState(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded">
            <option value="">All</option>
            {states.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <button type="button" onClick={load}
            className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!data && !error && (
        <p className="mt-2 text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Loading pipeline…</p>
      )}
      {data && data.campaigns.length === 0 && (
        <p className="mt-2 text-sm text-gray-500">No campaigns yet. A campaign is created from the programme's process template.</p>
      )}

      <div className="mt-2 space-y-3">
        {shown.map((c) => (
          <div key={c.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Link to={`/training/${encodeURIComponent(programId)}/campaigns/${encodeURIComponent(c.id)}`}
                className="text-sm font-medium text-accent hover:underline">
                <span className="text-xs text-gray-500 mr-1">{c.key}</span>{c.title}
              </Link>
              <div className="flex items-center gap-3">
                <StatusPill label={c.statusLabel} />
                <OpenItem id={c.id} />
                <span className="text-xs text-gray-500">{c.batches.length} batch(es) · {c.rounds.length} round(s)</span>
              </div>
            </div>
            <div className="mt-1"><Waiting waiting={c.waiting} compact /></div>
            <div className="mt-2 grid gap-3 lg:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Batches</div>
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">{c.batches.map((n) => <NodeRow key={n.id} node={n} />)}</ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Rounds</div>
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">{c.rounds.map((n) => <NodeRow key={n.id} node={n} />)}</ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
